import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Grid, List, ArrowRight } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import languageLearningApi from '../middleware/languageLearningApi'; // Adjust path as needed


// Updated CSS with consistent styling
const styles = `
/* Global styles with enhanced color palette */
:root {
  --primary-color: #1e3a8a; /* Navy Blue */
  --primary-hover: #1e40af; /* Slightly brighter navy */
  --primary-light: #e0e7ff; /* Soft blue for background highlights */
  --card-bg: #ffffff;
  --card-border: #f3f4f6;
  --card-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --card-hover-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --text-primary: #0f172a; /* Deep slate */
  --text-secondary: #64748b; /* Cool gray */
  --bg-page: #f1f5f9;
  --bg-toggle: #e2e8f0;
  --toggle-active: #ffffff;
  --hover-bg: #e0e7ff;
  --border-color: #e2e8f0;
  --accent-color: #2563eb; /* Bold blue */
  --accent-light: #dbeafe;
  --heading-gradient-from:rgb(11, 33, 95);
  --heading-gradient-to: #2563eb;
  --success-bg: #10b981; /* Green for coming soon tag */
  --success-text: #ffffff;
}

body {
  background-color: var(--bg-page);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.language-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.heading {
  font-size: 2.25rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-primary);
  letter-spacing: -0.025em;
  background: linear-gradient(135deg, var(--heading-gradient-from), var(--heading-gradient-to));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.subheading {
  text-align: center;
  color: var(--text-secondary);
  margin-top: -1.5rem;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

/* Toggle Styles */
.view-toggle {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

.toggle-container {
  background-color: var(--bg-toggle);
  border-radius: 0.5rem;
  padding: 0.25rem;
  display: inline-flex;
  border: 1px solid var(--border-color);
}

.toggle-button {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  color: var(--text-secondary);
}

.toggle-button.active {
  background-color: var(--toggle-active);
  color: var(--primary-color);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

/* Card Styles with consistent styling */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.language-card {
  background-color: var(--card-bg);
  border-radius: 0.75rem;
  box-shadow: var(--card-shadow);
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--card-border);
  overflow: hidden;
  height: 120px;
  position: relative; /* For positioning the coming soon tag */
  /* Making all cards consistent with the clickable style */
  background: linear-gradient(to right, white, var(--primary-light));
}

.language-card.available:hover {
  box-shadow: var(--card-hover-shadow);
  transform: translateY(-4px);
  background: linear-gradient(to right, white, var(--accent-light));
}

.language-card.coming-soon {
  cursor: default;
  opacity: 0.9;
}

.coming-soon-tag {
  position: absolute;
  top: 0;
  right: 0;
  background-color: var(--success-bg);
  color: var(--success-text);
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 0 0.5rem 0 0.5rem;
  letter-spacing: 0.03em;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.flag-container {
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.flag-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-content {
  display: flex;
  align-items: center;
}

.language-info {
  flex: 1;
}

.native-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.english-name {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.click-indicator {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--primary-color);
  font-weight: 500;
  padding: 4px 8px;
  background-color: var(--primary-light);
  border-radius: 4px;
  display: inline-flex;
}

.click-indicator svg {
  margin-left: 0.25rem;
  transition: transform 0.2s ease;
}

.language-card.available:hover .click-indicator svg {
  transform: translateX(3px);
}

/* Simplified List Styles */
.list-container {
  background-color: var(--card-bg);
  border-radius: 0.75rem;
  box-shadow: var(--card-shadow);
  overflow: hidden;
}

.language-table {
  width: 100%;
  border-collapse: collapse;
}

.language-table th {
  text-align: left;
  padding: 1rem;
  font-weight: 500;
  color: var(--text-secondary);
  background-color: var(--primary-light);
  border-bottom: 1px solid var(--border-color);
}

.language-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.language-table tr:last-child td {
  border-bottom: none;
}

/* Only available rows are clickable now */
.language-table tr.available {
  cursor: pointer;
}

.language-table tr.available:hover {
  background-color: var(--hover-bg);
}

.language-table tr.coming-soon {
  opacity: 0.8;
  cursor: default;
}

.table-flag {
  padding: 0.5rem;
  width: 48px;
}

.table-flag img {
  width: 36px;
  height: 24px;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.table-native {
  font-weight: 600;
  color: var(--text-primary);
}

.table-english {
  color: var(--text-secondary);
}

.table-coming-soon {
  color: var(--success-bg);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background-color: rgba(16, 185, 129, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  display: inline-block;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  .heading {
    font-size: 1.875rem;
  }
}

@media (max-width: 480px) {
  .card-grid {
    grid-template-columns: 1fr;
  }
  
  .toggle-button span {
    display: none;
  }
  
  .toggle-button {
    padding: 0.5rem;
  }
}
`;

const LanguageSelector = ({tokenFromApp = null}) => {
  const [viewMode, setViewMode] = useState('card');
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get token from localStorage on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token'); // Adjust if token is stored elsewhere
    if (storedToken) {
      setToken(storedToken);
      try {
        const decoded = jwtDecode(storedToken);
        if (decoded && decoded.user && decoded.user._id) {
          setUserId(decoded.user._id);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    } else {
      console.log('No stored token found!');
      if(tokenFromApp){
        console.log('Found token through App token propagation ');
        const decoded = jwtDecode(tokenFromApp);
        if (decoded && decoded.user && decoded.user._id) {
          setUserId(decoded.user._id);
        }
      }
    }
  }, []);


  // Language data with only necessary properties, standardized and including availability status
  const languages = [
    {
      id: '67cdeb80b567cb0052c62148',
      nativeName: 'Deutsch',
      englishName: 'German',
      flagPath: 'https://flagcdn.com/w80/de.png',
      available: true
    },
    {
      id: '67ff634626c001d218da332b',
      nativeName: 'Español',
      englishName: 'Spanish',
      flagPath: 'https://flagcdn.com/w80/es.png',
      available: true
    },
    {
      id: '6801e9392e9e345122975c83',
      nativeName: 'Français',
      englishName: 'French',
      flagPath: 'https://flagcdn.com/w80/fr.png',
      available: true
    },
    {
      id: 'japanese-id',
      nativeName: '日本語',
      englishName: 'Japanese',
      flagPath: 'https://flagcdn.com/w80/jp.png',
      available: false
    },
    {
      id: 'chinese-id',
      nativeName: '中文',
      englishName: 'Chinese',
      flagPath: 'https://flagcdn.com/w80/cn.png',
      available: false
    },
    {
      id: 'korean-id',
      nativeName: '한국어',
      englishName: 'Korean',
      flagPath: 'https://flagcdn.com/w80/kr.png',
      available: false
    },
    {
      id: 'arabic-id',
      nativeName: 'العربية',
      englishName: 'Arabic',
      flagPath: 'https://flagcdn.com/w80/sa.png',
      available: false
    },
    {
      id: 'russian-id',
      nativeName: 'Русский',
      englishName: 'Russian',
      flagPath: 'https://flagcdn.com/w80/ru.png',
      available: false
    },
    {
      id: 'portuguese-id',
      nativeName: 'Português',
      englishName: 'Portuguese',
      flagPath: 'https://flagcdn.com/w80/pt.png',
      available: false
    }
  ];

  // Function to check if a goal exists for the selected language
  const checkGoalAndNavigate = async (language) => {
    if (!userId) {
      // If user is not logged in, redirect to login
      window.location.href = '/login';
      return;
    }
    
    setLoading(true);
    try {
      // Check if a goal exists for this language
      const goalResponse = await languageLearningApi.get(`/goals/goals/${userId}/${language.id}`);
      
      if (goalResponse.data && goalResponse.data.goal) {
        // Goal exists, navigate to language dashboard
        window.location.href = `/language-dashboard/${language.id}`;
      } else {
        // No goal exists, navigate to goal setting page
        window.location.href = `/set-daily-goal?courseId=${language.id}`;
      }
    } catch (error) {
      // Assume no goal exists if there's an error
      window.location.href = `/set-daily-goal?courseId=${language.id}`;
    } finally {
      setLoading(false);
    }
  };

  // Function to handle language card/row click
  const handleLanguageClick = (language) => {
    // Only navigate if the language is available
    if (language.available) {
      checkGoalAndNavigate(language);
    }
  };

  // Animation variants for cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="language-container">
        <h1 className="heading">Language Selector</h1>
        <p className="subheading">Choose a language to start your learning journey</p>

        {/* View Toggle */}
        <div className="view-toggle">
          <div className="toggle-container">
            <button
              className={`toggle-button ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              <Grid size={18} />
              <span>Card View</span>
            </button>
            <button
              className={`toggle-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
              <span>List View</span>
            </button>
          </div>
        </div>

        {/* Card View - Different styling for available vs coming soon */}
        {viewMode === 'card' && (
          <motion.div
            className="card-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {languages.map((language) => (
              <motion.div
                key={language.id}
                className={`language-card ${language.available ? 'available' : 'coming-soon'}`}
                onClick={() => handleLanguageClick(language)}
                variants={cardVariants}
                whileHover={language.available ? { scale: 1.02 } : {}}
              >
                {!language.available && (
                  <div className="coming-soon-tag">Coming Soon!</div>
                )}
                <div className="card-content">
                  <div className="flag-container">
                    <img
                      src={language.flagPath}
                      alt={`${language.englishName} flag`}
                      className="flag-image"
                    />
                  </div>
                  <div className="language-info">
                    <div className="native-name">{language.nativeName}</div>
                    <div className="english-name">{language.englishName}</div>
                    {language.available && (
                      <div className="click-indicator">
                        Explore <ArrowRight size={14} />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* List View - Modified for available vs coming soon */}
        {viewMode === 'list' && (
          <motion.div
            className="list-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <table className="language-table">
              <thead>
                <tr>
                  <th width="70"></th>
                  <th>Native Name</th>
                  <th>English Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {languages.map((language, index) => (
                  <motion.tr
                    key={language.id}
                    className={language.available ? 'available' : 'coming-soon'}
                    onClick={() => handleLanguageClick(language)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { delay: index * 0.05 }
                    }}
                  >
                    <td className="table-flag">
                      <img
                        src={language.flagPath}
                        alt={`${language.englishName} flag`}
                      />
                    </td>
                    <td className="table-native">{language.nativeName}</td>
                    <td className="table-english">{language.englishName}</td>
                    <td>
                      {language.available ? (
                        <span className="click-indicator">
                          Explore <ArrowRight size={14} />
                        </span>
                      ) : (
                        <span className="table-coming-soon">Coming Soon</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default LanguageSelector;